import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export function initSentry() {
  // Only initialize if DSN is provided
  if (!SENTRY_DSN) {
    console.info("[Sentry] No DSN provided, error tracking disabled");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // 'development' or 'production'
    
    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Capture 10% of all sessions
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    
    // Performance tracing sample rate
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.2 : 1.0,
    
    // Session Replay sample rates
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Filter out known non-critical errors
    beforeSend(event) {
      // Ignore network errors that are expected
      if (event.exception?.values?.[0]?.value?.includes("Network Error")) {
        return null;
      }
      return event;
    },
  });

  console.info("[Sentry] Error tracking initialized");
}

// Export Sentry for use in components
export { Sentry };

// Error boundary wrapper for React components
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Capture custom errors manually
export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, { extra: context });
}

// Set user context for error tracking
export function setUser(user: { id: string; email?: string } | null) {
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email });
  } else {
    Sentry.setUser(null);
  }
}
